import React, { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ClipboardPaste, ImageIcon, Loader2, Trash2, Upload } from 'lucide-react'
import {
  ERP_IMAGE_TYPES,
  imageFromDataTransfer,
  namedFile,
  validateErpImage,
} from '../imageUtils'

interface ErpImagePanelProps {
  /** Image currently saved on the mapping (null after a staged removal). */
  imageUrl: string | null
  /** Local preview of a file staged for upload on save (create mode). */
  pendingPreviewUrl: string | null
  uploading: boolean
  onFile: (file: File) => void
  onRemove: () => void
  /** Listen for ⌘V/Ctrl+V anywhere in this element's document while mounted. */
  pasteTarget?: React.RefObject<HTMLElement>
}

/**
 * The ERP schematic image: large preview plus paste (⌘V or the button),
 * drag & drop and a file picker.
 */
export function ErpImagePanel({
  imageUrl,
  pendingPreviewUrl,
  uploading,
  onFile,
  onRemove,
  pasteTarget,
}: ErpImagePanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const previewUrl = pendingPreviewUrl || imageUrl
  const canReadClipboard =
    typeof navigator !== 'undefined' && !!navigator.clipboard?.read

  const accept = useCallback(
    (file: File | null) => {
      if (!file) return
      const error = validateErpImage(file)
      if (error) {
        toast.error(error)
        return
      }
      onFile(file)
    },
    [onFile]
  )

  // ⌘V / Ctrl+V anywhere in the sheet. Text pastes into inputs are left
  // alone; only clipboard data carrying an image is taken.
  useEffect(() => {
    const root = pasteTarget?.current ?? document
    const handler = (event: Event) => {
      const e = event as ClipboardEvent
      const file = imageFromDataTransfer(e.clipboardData)
      if (!file) return
      e.preventDefault()
      accept(file)
    }
    root.addEventListener('paste', handler)
    return () => root.removeEventListener('paste', handler)
  }, [accept, pasteTarget])

  const pasteFromButton = async () => {
    try {
      const items = await navigator.clipboard.read()
      for (const item of items) {
        const type = item.types.find(t => t.startsWith('image/'))
        if (type) {
          accept(namedFile(await item.getType(type), type))
          return
        }
      }
      toast.error('No image on the clipboard — copy the picture in Excel first')
    } catch (error) {
      console.error('Clipboard read failed:', error)
      toast.error(
        'Could not read the clipboard here — click the image box and press ⌘V / Ctrl+V'
      )
    }
  }

  return (
    <div className="space-y-2">
      <div
        tabIndex={0}
        className={cn(
          'relative flex h-64 items-center justify-center overflow-hidden rounded-md border-2 border-dashed bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] outline-none focus-visible:ring-2 focus-visible:ring-ring',
          dragOver && 'border-primary bg-primary/5'
        )}
        onDragOver={e => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault()
          setDragOver(false)
          accept(imageFromDataTransfer(e.dataTransfer))
        }}
        title="Paste (⌘V / Ctrl+V), drop or choose an image"
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="ERP schematic"
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 px-6 text-center text-sm text-muted-foreground">
            <ImageIcon className="h-8 w-8" />
            <span>
              Copy the schematic picture in Excel, then press{' '}
              <kbd className="rounded border bg-muted px-1 text-xs">⌘V</kbd> /{' '}
              <kbd className="rounded border bg-muted px-1 text-xs">Ctrl+V</kbd>{' '}
              here — or drop a file.
            </span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {pendingPreviewUrl && !uploading && (
          <span className="absolute left-2 top-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
            Uploads on save
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {canReadClipboard && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={pasteFromButton}
            disabled={uploading}
          >
            <ClipboardPaste className="mr-1.5 h-4 w-4" />
            Paste image
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Upload className="mr-1.5 h-4 w-4" />
          Choose file
        </Button>
        {previewUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={onRemove}
            disabled={uploading}
          >
            <Trash2 className="mr-1.5 h-4 w-4" />
            Remove
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ERP_IMAGE_TYPES.join(',')}
          className="hidden"
          onChange={e => {
            accept(e.target.files?.[0] ?? null)
            e.target.value = ''
          }}
        />
      </div>
    </div>
  )
}
